<template>
  <div id="importar-acompanhamento">
    <Menu />

    <div class="container main">
      <div class="row">
        <div class="col-md-12">
          <SectionTitle
            title="Importar acompanhamento"
            description="Importar arquivo de acompanhamento do Bolsa Família"
            url="/bolsa-familia"
            :enabledModalInformacao="true"
            :enabledIframeModalInformacao="true"
            urlIframeModalInformacao="https://doc.clickup.com/9011510980/d/h/8cj1fp4-1631/599ea88448ce3d9/8cj1fp4-9731"
            pageIdentifier="bolsa-familia-importar-acompanhamento"
          />
        </div>

        <div class="col-md-12">
          <div class="card mb-4">
            <div class="card-body">
              <h5 class="mb-3">Acompanhamento</h5>

              <b-form-file
                v-model="arquivo"
                browse-text="Selecionar"
                placeholder="Selecione o arquivo de acompanhamento..."
                drop-placeholder="Solte o arquivo aqui..."
                accept=".xls,.xlsx,.csv"
                @input="previewArquivo"
              ></b-form-file>

              <div v-if="preview.length" class="table-responsive mt-4">
                <b-alert show variant="info" class="mb-3">
                  <i class="icofont-info-circle mr-2"></i>
                  Esta prévia exibe apenas
                  <strong>2 registros</strong>
                  do arquivo para conferência. Todos os registros serão
                  importados.
                </b-alert>

                <h6>Prévia do arquivo</h6>

                <table class="table table-sm table-bordered table-preview">
                  <thead>
                    <tr>
                      <th v-for="(coluna, index) in preview[0]" :key="index">
                        {{ coluna }}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr v-for="(linha, index) in preview.slice(1)" :key="index">
                      <td
                        v-for="(coluna, colunaIndex) in linha"
                        :key="colunaIndex"
                      >
                        {{ coluna }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <button
                class="btn btn-primary mt-3"
                :disabled="isLoading || !preview.length"
                @click="importar"
              >
                <template v-if="isLoading">
                  <b-spinner small class="mr-2"></b-spinner>
                  Importando acompanhamento...
                </template>

                <template v-else> Importar acompanhamento </template>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import Menu from '@/components/Menu'
import Footer from '@/components/Footer'
import SectionTitle from '@/components/SectionTitle'

export default {
  name: 'ImportarAcompanhamento',
  components: {
    Menu,
    Footer,
    SectionTitle,
  },
  data() {
    return {
      arquivo: null,
      preview: [],
      isLoading: false,
    }
  },
  methods: {
    /**
     * Lê o arquivo selecionado e retorna as linhas da primeira aba.
     *
     * @param {File} arquivo Arquivo selecionado.
     * @returns {Promise<Array>}
     */
    lerArquivo(arquivo) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = (event) => {
          try {
            const data = new Uint8Array(event.target.result)
            const workbook = XLSX.read(data, { type: 'array' })
            const sheetName = workbook.SheetNames[0]
            const worksheet = workbook.Sheets[sheetName]

            const linhas = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              defval: '',
            })

            resolve(linhas)
          } catch (error) {
            reject(error)
          }
        }

        reader.onerror = reject
        reader.readAsArrayBuffer(arquivo)
      })
    },

    /**
     * Monta a prévia do arquivo de acompanhamento.
     *
     * Retorna o cabeçalho e os dois primeiros registros válidos.
     *
     * @param {Array} linhas Linhas da planilha.
     * @returns {Array}
     */
    montarPreview(linhas) {
      return linhas
        .slice(0, 3)
        .filter((linha) => linha.some((coluna) => String(coluna).trim() !== ''))
    },

    /**
     * Gera a prévia do arquivo de acompanhamento selecionado.
     *
     * @returns {Promise<void>}
     */
    async previewArquivo() {
      this.preview = []

      if (!this.arquivo) {
        return
      }

      try {
        const linhas = await this.lerArquivo(this.arquivo)

        this.preview = this.montarPreview(linhas)

        if (!this.preview.length) {
          this.arquivo = null
          this.$toast?.error?.('O arquivo selecionado está vazio.')
        }
      } catch (error) {
        console.error(error)

        this.arquivo = null
        this.$toast?.error?.('Erro ao ler o arquivo de acompanhamento.')
      }
    },

    /**
     * Importa o arquivo de acompanhamento.
     *
     * @returns {Promise<void>}
     */
    async importar() {
      if (!this.arquivo) {
        this.$toast?.error?.('Selecione o arquivo de acompanhamento.')
        return
      }

      const formData = new FormData()

      formData.append('co_ibge', this.$store.state.cod_ibge)
      formData.append('arquivo', this.arquivo)

      this.isLoading = true

      try {
        const response = await this.$api.post(
          '/app/bolsa-familia/importar-acompanhamento',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        )

        this.$toast?.success?.(response.data.message)

        this.arquivo = null
        this.preview = []
      } catch (error) {
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
#importar-acompanhamento {
  .table-responsive {
    display: inherit;
    margin-bottom: 0;
  }
}
</style>
